/**
 * Shared template resolver – single source of truth for runtime template availability.
 *
 * `resolveAvailableTemplates` is a pure function (testable without React).
 * `useResolvedTemplates` is a hook that fetches from Medplum and filters via the resolver.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire } from '@medplum/fhirtypes';
import {
  TEMPLATE_CATEGORIES,
  type DocumentationSettings,
  type TemplateCategory,
  type TemplateUsageTarget,
  DEFAULT_DOCUMENTATION_SETTINGS,
} from '../types';
import { getTemplateCategory, isDefaultTemplate } from '../utils/templateTags';
import { useDocumentationSettings } from './useDocumentationSettings';
import { searchQuestionnairesByCategory, dedupeQuestionnaires } from '../utils/searchQuestionnaireTemplates';

// ---------------------------------------------------------------------------
// Pure resolver
// ---------------------------------------------------------------------------

export interface ResolveTemplatesParams {
  allTemplates: Questionnaire[];
  settings: DocumentationSettings;
  target: TemplateUsageTarget;
  category?: TemplateCategory;
  adminMode?: boolean;
}

export function resolveAvailableTemplates(params: ResolveTemplatesParams): Questionnaire[] {
  const { allTemplates, settings, target, category, adminMode } = params;
  let result = [...allTemplates];

  // 1. Status filtering
  if (!adminMode) {
    const allowDraft = settings.templateRules.includeDraftsInRuntime;
    result = result.filter((q) => {
      const status = q.status ?? 'draft';
      if (status === 'active') return true;
      if (status === 'draft' && allowDraft) return true;
      return false;
    });
  }

  // 2. Category filtering: exclude templates from disabled categories
  if (!adminMode) {
    result = result.filter((q) => {
      const cat = getTemplateCategory(q);
      if (!cat) return true;
      return settings.enabledCategories[cat] !== false;
    });
  }

  // 3. If a specific category was requested, filter to it
  if (category) {
    result = result.filter((q) => {
      const cat = getTemplateCategory(q);
      return cat === category;
    });
  } else if (target === 'appointment-note' && !adminMode) {
    result = result.filter((q) => {
      const cat = getTemplateCategory(q);
      return !cat || cat === 'appointments';
    });
  }

  // 4. Sort: defaults first, then alphabetically by title
  const defaultForContext = settings.defaultTemplateByContext.find(
    (d) =>
      d.target === target &&
      (!d.category || d.category === category),
  );

  result.sort((a, b) => {
    const aIsContextDefault = defaultForContext?.templateId === a.id;
    const bIsContextDefault = defaultForContext?.templateId === b.id;
    if (aIsContextDefault && !bIsContextDefault) return -1;
    if (!aIsContextDefault && bIsContextDefault) return 1;

    const aIsDefault = isDefaultTemplate(a);
    const bIsDefault = isDefaultTemplate(b);
    if (aIsDefault && !bIsDefault) return -1;
    if (!aIsDefault && bIsDefault) return 1;

    const aTitle = (a.title ?? a.name ?? '').toLowerCase();
    const bTitle = (b.title ?? b.name ?? '').toLowerCase();
    return aTitle.localeCompare(bTitle);
  });

  return result;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseResolvedTemplatesOptions {
  target: TemplateUsageTarget;
  category?: TemplateCategory;
  adminMode?: boolean;
  skip?: boolean;
}

export interface UseResolvedTemplatesResult {
  templates: Questionnaire[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const CATEGORY_FOR_TARGET: Partial<Record<TemplateUsageTarget, TemplateCategory[]>> = {
  'appointment-note': ['appointments'],
  'medical-record-note': ['appointments', 'administrative'],
  assessment: ['measures'],
  'client-portal-form': ['forms', 'measures'],
  'treatment-plan': ['treatment-planning'],
  'safety-plan': ['administrative'],
  'risk-assessment': ['administrative'],
  'admin-note': ['administrative'],
  'discharge-summary': ['administrative'],
  'supervision-review': ['appointments'],
  'document-preview': undefined,
  'chart-document': ['treatment-planning', 'administrative', 'forms'],
};

export function useResolvedTemplates(options: UseResolvedTemplatesOptions): UseResolvedTemplatesResult {
  const { target, category, adminMode, skip = false } = options;
  const medplum = useMedplum();
  const { settings, loading: settingsLoading } = useDocumentationSettings();
  const [allTemplates, setAllTemplates] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const [fetchCounter, setFetchCounter] = useState(0);

  const refetch = useCallback(() => setFetchCounter((c) => c + 1), []);

  const categoriesToFetch = useMemo(() => {
    if (category) return [category];
    return CATEGORY_FOR_TARGET[target] ?? [...TEMPLATE_CATEGORIES];
  }, [target, category]);
  const categoriesJoined = categoriesToFetch.join(',');

  useEffect(() => {
    if (skip) {
      setAllTemplates([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    async function doFetch() {
      try {
        medplum.invalidateSearches('Questionnaire');
        const statusParam = adminMode ? 'active,draft,retired' : 'active,draft';
        const results = await Promise.all(
          categoriesToFetch.map((cat) =>
            searchQuestionnairesByCategory(medplum, cat, {
              status: statusParam,
              _sort: 'title',
              _count: '100',
            }),
          ),
        );
        if (cancelled) return;

        setAllTemplates(dedupeQuestionnaires(results.flat()));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch templates');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    doFetch();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, skip, adminMode, categoriesJoined, fetchCounter]);

  const resolved = useMemo(
    () =>
      resolveAvailableTemplates({
        allTemplates,
        settings: settingsLoading ? DEFAULT_DOCUMENTATION_SETTINGS : settings,
        target,
        category,
        adminMode,
      }),
    [allTemplates, settings, settingsLoading, target, category, adminMode],
  );

  return {
    templates: resolved,
    loading: loading || settingsLoading,
    error,
    refetch,
  };
}
